import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import config from "../../config.js";
import {
  initMapKit,
  initMapKitAsync,
  searchPlaces,
  getUserLocation,
} from "../../utils/mapkit.js";
import { CrosshairsIcon } from "./Icons.js";
import { Button } from "../ui/Button";

export const LocationPicker = ({ onLocationSelect, initialAddress }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isMapKitLoaded, setIsMapKitLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);

  const searchTimeoutRef = useRef(null);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const syncLoaded = initMapKit(config.mapKitToken);
    if (syncLoaded) {
      setIsMapKitLoaded(true);
    } else {
      initMapKitAsync(config.mapKitToken).then((loaded) => {
        if (isMounted && loaded) {
          setIsMapKitLoaded(true);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddressSearch = (query) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query || !isMapKitLoaded) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      const places = await searchPlaces(query, userLocation, 0.5);
      setSearchResults(places);
    }, 500);
  };

  const handleUseLocation = async () => {
    setIsLocating(true);
    try {
      const locData = await getUserLocation();
      const { latitude, longitude, results } = locData;
      setUserLocation({ latitude, longitude });

      let selectedAddr = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      let selectedName = "Current Location";

      if (results && results.length > 0) {
        const topRes = results[0];
        selectedName = topRes.name || selectedName;
        selectedAddr = topRes.formattedAddress || selectedAddr;
      }

      onLocationSelect({
        name: selectedName,
        address: selectedAddr,
      });
      setSearchQuery(selectedAddr);

      if (mapRef.current && globalThis.mapkit) {
        const center = new globalThis.mapkit.Coordinate(latitude, longitude);
        const span = new globalThis.mapkit.CoordinateSpan(0.005, 0.005);
        mapRef.current.region = new globalThis.mapkit.CoordinateRegion(
          center,
          span,
        );
        if (mapRef.current.annotations) {
          mapRef.current.removeAnnotations(mapRef.current.annotations);
        }
        const annotation = new globalThis.mapkit.MarkerAnnotation(center, {
          title: selectedName,
          subtitle: selectedAddr,
          color: "#40e0d0",
        });
        mapRef.current.addAnnotation(annotation);
      }
    } catch (error) {
      console.warn("Geolocation error:", error);
    } finally {
      setIsLocating(false);
    }
  };

  const searchAndPin = useCallback((address, map) => {
    if (!globalThis.mapkit || !address) return;
    const search = new globalThis.mapkit.Search();
    search.search(address, (error, data) => {
      if (error || !data.places || data.places.length === 0) return;
      const place = data.places[0];
      const coordinate = place.coordinate;
      map.region = new globalThis.mapkit.CoordinateRegion(
        coordinate,
        new globalThis.mapkit.CoordinateSpan(0.01, 0.01),
      );
      if (map.annotations && typeof map.removeAnnotations === "function") {
        map.removeAnnotations(map.annotations);
      }
      const annotation = new globalThis.mapkit.MarkerAnnotation(coordinate, {
        title: place.name || address,
        subtitle: place.formattedAddress,
        color: "#40e0d0",
      });
      map.addAnnotation(annotation);
    });
  }, []);

  useEffect(() => {
    let timeoutId;
    let observer;

    if (isMapKitLoaded) {
      const mapContainer = mapContainerRef.current;

      if (mapContainer && globalThis.mapkit) {
        const initMap = () => {
          if (mapRef.current) return;
          try {
            const map = new globalThis.mapkit.Map(mapContainer, {
              showsUserLocationControl: false,
              showsZoomControl: true,
            });
            mapRef.current = map;

            const centerLat = userLocation ? userLocation.latitude : 37.0902;
            const centerLn = userLocation ? userLocation.longitude : -95.7129;
            const spanDelta = userLocation ? 0.5 : 50;

            const center = new globalThis.mapkit.Coordinate(
              centerLat,
              centerLn,
            );
            const span = new globalThis.mapkit.CoordinateSpan(
              spanDelta,
              spanDelta,
            );
            map.region = new globalThis.mapkit.CoordinateRegion(center, span);

            if (initialAddress) {
              searchAndPin(initialAddress, map);
            }
          } catch (e) {
            console.warn("MapKit init map exception:", e);
          }
        };

        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
              if (
                entry.contentRect.width > 0 &&
                entry.contentRect.height > 0 &&
                !mapRef.current
              ) {
                initMap();
                if (observer) observer.disconnect();
              }
            }
          });
          observer.observe(mapContainer);
        } else {
          initMap();
        }
      }
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (timeoutId) clearTimeout(timeoutId);
      if (mapRef.current) {
        try {
          if (typeof mapRef.current.destroy === "function") {
            mapRef.current.destroy();
          }
        } catch (e) {
          // ignore
        }
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapKitLoaded]);

  useEffect(() => {
    if (mapRef.current && initialAddress && globalThis.mapkit) {
      searchAndPin(initialAddress, mapRef.current);
    }
  }, [initialAddress, searchAndPin]);

  const selectAddress = (place) => {
    const selectedAddr = place.formattedAddress || place.name || "";
    const selectedName = place.name || "";

    onLocationSelect({
      name: selectedName,
      address: selectedAddr,
    });
    setSearchQuery(place.name || selectedAddr);
    setSearchResults([]);

    if (mapRef.current && place.coordinate && globalThis.mapkit) {
      const center = place.coordinate;
      const span = new globalThis.mapkit.CoordinateSpan(0.005, 0.005);
      const region = new globalThis.mapkit.CoordinateRegion(center, span);
      mapRef.current.region = region;

      if (mapRef.current.annotations) {
        mapRef.current.removeAnnotations(mapRef.current.annotations);
      }
      const annotation = new globalThis.mapkit.MarkerAnnotation(center, {
        title: selectedName || selectedAddr,
        subtitle: selectedAddr,
        color: "#40e0d0",
      });
      mapRef.current.addAnnotation(annotation);
    }
  };

  return (
    <div className="location-picker">
      <div className="search-wrapper">
        <div className="search-input-group location-picker-input-group">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleAddressSearch(e.target.value)}
            placeholder="Search for a place or address..."
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            className="search-input location-picker-input"
          />
          <Button
            className="icon-btn location-picker-crosshairs"
            onClick={handleUseLocation}
            disabled={isLocating}
            title={isLocating ? "Locating..." : "Use my location"}
            type="button"
          >
            <CrosshairsIcon />
          </Button>
        </div>
        {searchResults.length > 0 && (
          <ul className="search-results location-picker-dropdown">
            {searchResults.map((place, idx) => (
              <li
                key={place.muid || idx}
                className="location-picker-dropdown-item"
              >
                <Button
                  type="button"
                  onClick={() => selectAddress(place)}
                  className="location-picker-dropdown-btn"
                >
                  <div
                    className="search-result-name"
                    style={{ fontWeight: 600 }}
                  >
                    {place.name}
                  </div>
                  <div
                    className="search-result-address"
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {place.formattedAddress}
                  </div>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        ref={mapContainerRef}
        id="location-picker-map"
        className="map-container location-picker-map"
      ></div>

      {!isMapKitLoaded && config.mapKitToken && (
        <small
          className="text-muted map-init-text"
          style={{ display: "block", marginTop: "4px" }}
        >
          Map search initializing...
        </small>
      )}
    </div>
  );
};

LocationPicker.propTypes = {
  onLocationSelect: PropTypes.func.isRequired,
  initialAddress: PropTypes.string,
};
